/**
 * Collections showcase — "תצוגת קולקציות"
 *
 * Two custom elements:
 *   <collections-showcase-tabs> — switches between collection panels
 *   <cs-carousel>               — horizontal product carousel with arrows
 *
 * Arrow semantics are PHYSICAL: the left button scrolls the viewport left,
 * the right button scrolls right, matching glyphs that natively point left
 * and right. The only RTL subtlety is the scrollLeft range: LTR is [0, max],
 * RTL is [-max, 0] in modern browsers.
 */

class CollectionsShowcaseTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('.collections-showcase__tab'));
    this.panels = Array.from(this.querySelectorAll('.collections-showcase__panel'));

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab.dataset.index));
      tab.addEventListener('keydown', (event) => this.onKeydown(event));
    });
  }

  select(index) {
    this.tabs.forEach((tab) => {
      tab.setAttribute('aria-selected', tab.dataset.index === index ? 'true' : 'false');
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.index !== index;
    });

    // Panels start hidden, so their carousels measured a zero width on connect.
    // Re-measure the one we just revealed.
    this.panels
      .filter((panel) => !panel.hidden)
      .forEach((panel) => {
        panel.querySelectorAll('cs-carousel').forEach((carousel) => carousel.updateArrows?.());
      });
  }

  onKeydown(event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

    event.preventDefault();

    // RTL-aware: in rtl, ArrowLeft moves forward through the tabs.
    const isRtl = getComputedStyle(this).direction === 'rtl';
    const forwardKey = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const step = event.key === forwardKey ? 1 : -1;

    const current = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const next = (current + step + this.tabs.length) % this.tabs.length;

    this.select(this.tabs[next].dataset.index);
    this.tabs[next].focus();
  }
}

class CsCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-cs-track]');
    this.belt = this.querySelector('[data-cs-belt]');
    this.leftBtn = this.querySelector('[data-cs-action="left"]');
    this.rightBtn = this.querySelector('[data-cs-action="right"]');
    // Optional "navigation below" row (snippets/carousel-nav-below.liquid).
    this.navBelow = this.querySelector('[data-cs-nav-below]');
    this.dotsWrap = this.querySelector('[data-cs-dots]');

    if (!this.track) return;

    this.leftBtn?.addEventListener('click', () => this.scrollByCard(-1));
    this.rightBtn?.addEventListener('click', () => this.scrollByCard(1));

    this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
    window.addEventListener('resize', () => this.updateArrows(), { passive: true });

    // Images load after connect and change scrollWidth, so re-check on resize.
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => {
        this.updateArrows();

        // Marquee period depends on content width — rebuild when it changes.
        if (this.marqueeMode && this.buildMarquee && this.belt) {
          const half = this.belt.scrollWidth / 2;
          if (Math.abs(half - (this.marqueeHalf || 0)) > 1) this.buildMarquee();
        }
      });
      this.observer.observe(this.track);
      if (this.querySelector('[data-cs-belt]')) this.observer.observe(this.querySelector('[data-cs-belt]'));
    }

    // Defer the first pass: on connect the track may not be laid out yet
    // (width 0), which would make every measurement wrong.
    requestAnimationFrame(() => this.updateArrows());

    // Opt-in infinite marquee (reviews section): content is rendered twice
    // and the scroll position wraps seamlessly at the halfway point.
    if (this.dataset.csAutoplay) {
      this.initMarquee(parseFloat(this.dataset.csAutoplay) || 30);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.marqueeAnim?.cancel();
  }

  /**
   * Continuous marquee at `speed` px/s. Pauses on hover, focus, and touch.
   *
   * Driven by the Web Animations API, NOT a rAF loop: a JS-driven animation
   * advances on the main thread, so any main-thread work (the theme editor,
   * image decoding, other scripts) shows up as visible micro-jumps. A WAAPI
   * transform animation runs on the compositor thread and stays smooth
   * regardless of main-thread jank.
   */
  initMarquee(speed) {
    if (!this.belt) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.marqueeMode = true;

    // Directions (translate moves CONTENT, the opposite of scrollLeft which
    // moves the viewport): RTL overflow extends to the LEFT of the window,
    // so forward motion moves the belt RIGHT; LTR the mirror.
    const dir = getComputedStyle(this).direction === 'rtl' ? 1 : -1;

    this.buildMarquee = () => {
      const half = this.belt.scrollWidth / 2;

      // Preserve progress across rebuilds (resize, images loading).
      let frac = 0;
      if (this.marqueeAnim) {
        const t = Number(this.marqueeAnim.currentTime) || 0;
        frac = this.marqueeDur ? (((t % this.marqueeDur) + this.marqueeDur) % this.marqueeDur) / this.marqueeDur : 0;
        this.marqueeAnim.cancel();
        this.marqueeAnim = null;
      }

      if (half <= this.track.clientWidth) return;

      this.marqueeHalf = half;
      this.marqueeDur = (half / speed) * 1000;

      // Content is duplicated, so one period of `half` px loops seamlessly.
      this.marqueeAnim = this.belt.animate(
        [{ transform: 'translate3d(0, 0, 0)' }, { transform: `translate3d(${dir * half}px, 0, 0)` }],
        { duration: this.marqueeDur, iterations: Infinity, easing: 'linear' }
      );
      this.marqueeAnim.currentTime = frac * this.marqueeDur;
      if (this.marqueePaused) this.marqueeAnim.pause();
    };

    const pause = () => {
      this.marqueePaused = true;
      this.marqueeAnim?.pause();
    };
    const resume = () => {
      this.marqueePaused = false;
      this.marqueeAnim?.play();
    };

    this.addEventListener('mouseenter', pause);
    this.addEventListener('mouseleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', resume);
    this.track.addEventListener(
      'pointerdown',
      () => {
        pause();
        clearTimeout(this.marqueeResume);
        this.marqueeResume = setTimeout(resume, 3000);
      },
      { passive: true }
    );

    // Build after layout so widths are real.
    requestAnimationFrame(() => this.buildMarquee());
  }

  /** Distance of one step — a single card width plus the column gap. */
  get step() {
    // Cards live either directly in the track or inside a marquee belt.
    const container = this.belt || this.track;
    const card = container.firstElementChild;
    if (!card) return this.track.clientWidth;

    const gap = parseFloat(getComputedStyle(container).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  get maxScroll() {
    return this.track.scrollWidth - this.track.clientWidth;
  }

  /** direction: -1 scrolls one card left, 1 scrolls one card right. Physical. */
  scrollByCard(direction) {
    if (this.marqueeMode) {
      if (!this.marqueeAnim) return;

      // Shift the animation phase by one card. Content moves opposite to the
      // viewing direction ("look left" = belt shifts right); the animation's
      // own direction sign is already baked into its keyframes, so looking
      // against it means rewinding the timeline.
      const dir = getComputedStyle(this).direction === 'rtl' ? 1 : -1;
      const deltaMs = ((-this.step * direction) / (dir * this.marqueeHalf)) * this.marqueeDur;

      let t = (Number(this.marqueeAnim.currentTime) || 0) + deltaMs;
      t = ((t % this.marqueeDur) + this.marqueeDur) % this.marqueeDur;
      this.marqueeAnim.currentTime = t;
      return;
    }

    this.track.scrollBy({ left: this.step * direction, behavior: 'smooth' });
  }

  /**
   * The buttons carry inline `display: none` (an inline display would defeat
   * the `hidden` attribute — this theme has no global [hidden] override), so
   * visibility is toggled through style.display.
   */
  setArrowsVisible(visible) {
    const display = visible ? 'flex' : 'none';
    if (this.leftBtn) this.leftBtn.style.display = display;
    if (this.rightBtn) this.rightBtn.style.display = display;
    if (this.navBelow) this.navBelow.style.display = display;
  }

  /** Number of dot "pages": how many viewport-widths the track spans. */
  get pageCount() {
    const width = this.track.clientWidth;
    if (!width) return 1;
    return Math.max(1, Math.ceil(this.track.scrollWidth / width - 0.05));
  }

  /**
   * Dots are built here (not in Liquid) because the page count depends on
   * the rendered widths — columns per breakpoint, images, window size.
   * Styles are inline so they can't be lost to a stale CSS bundle.
   */
  updateDots() {
    if (!this.dotsWrap || this.marqueeMode) return;

    const pages = this.pageCount;
    if (this.dotsWrap.childElementCount !== pages) {
      this.dotsWrap.replaceChildren(
        ...Array.from({ length: pages }, (_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'cs-nav-dot';
          dot.setAttribute('aria-label', `${i + 1}/${pages}`);
          dot.style.cssText =
            'width: 8px; height: 8px; padding: 0; margin: 0; border: none; border-radius: 50%; cursor: pointer; background-color: currentColor; opacity: 0.3; transition: opacity 0.2s ease, scale 0.2s ease;';
          dot.addEventListener('click', () => this.scrollToPage(i));
          return dot;
        })
      );
    }

    const max = this.maxScroll;
    const active = pages > 1 && max > 0 ? Math.round((Math.abs(this.track.scrollLeft) / max) * (pages - 1)) : 0;
    [...this.dotsWrap.children].forEach((dot, i) => {
      const isActive = i === active;
      dot.style.opacity = isActive ? '1' : '0.3';
      dot.style.scale = isActive ? '1.25' : '1';
      if (isActive) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  /** Page 0 is the start edge (visual right in RTL). */
  scrollToPage(index) {
    const pages = this.pageCount;
    const max = this.maxScroll;
    const target = pages > 1 ? (index / (pages - 1)) * max : 0;
    const isRtl = getComputedStyle(this).direction === 'rtl';
    this.track.scrollTo({ left: isRtl ? -target : target, behavior: 'smooth' });
  }

  updateArrows() {
    if (!this.leftBtn && !this.rightBtn && !this.navBelow) return;

    // Infinite marquee: both directions always available, no disabled states.
    if (this.marqueeMode) {
      this.setArrowsVisible(this.belt.scrollWidth / 2 > this.track.clientWidth);
      if (this.leftBtn) this.leftBtn.disabled = false;
      if (this.rightBtn) this.rightBtn.disabled = false;
      return;
    }

    const max = this.maxScroll;

    // No overflow — every product already fits, so there is nowhere to
    // navigate. Hide the arrows entirely instead of showing dead buttons.
    if (max <= 1) {
      this.setArrowsVisible(false);
      return;
    }

    this.setArrowsVisible(true);
    this.updateDots();

    // scrollLeft range: LTR [0, max], RTL [-max, 0].
    const isRtl = getComputedStyle(this).direction === 'rtl';
    const minX = isRtl ? -max : 0;
    const maxX = isRtl ? 0 : max;
    const x = this.track.scrollLeft;

    if (this.leftBtn) this.leftBtn.disabled = x <= minX + 1;
    if (this.rightBtn) this.rightBtn.disabled = x >= maxX - 1;
  }
}

/**
 * <cs-hero> — fading hero slideshow (hero-media section).
 * Auto-advances every data-interval seconds; dots or arrows navigate.
 * Videos play only on their active slide.
 */
class CsHero extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-hero-slide]')];
    this.dots = [...this.querySelectorAll('[data-hero-dot]')];
    this.index = Math.max(0, this.slides.findIndex((s) => s.hasAttribute('data-active')));

    if (this.slides.length < 2) return;

    this.querySelector('[data-hero-action="left"]')?.addEventListener('click', () => this.step(1));
    this.querySelector('[data-hero-action="right"]')?.addEventListener('click', () => this.step(-1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

    const seconds = parseFloat(this.dataset.interval) || 5;
    this.timer = setInterval(() => {
      if (document.visibilityState === 'visible') this.goTo((this.index + 1) % this.slides.length);
    }, seconds * 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  /** RTL site: forward reads leftward, so the left arrow advances. */
  step(direction) {
    this.goTo((this.index + direction + this.slides.length) % this.slides.length);
    // Manual navigation restarts the clock so the next auto-advance
    // doesn't fire right after a click.
    const seconds = parseFloat(this.dataset.interval) || 5;
    clearInterval(this.timer);
    this.timer = setInterval(() => {
      if (document.visibilityState === 'visible') this.goTo((this.index + 1) % this.slides.length);
    }, seconds * 1000);
  }

  goTo(i) {
    this.index = i;

    this.slides.forEach((slide, n) => {
      const active = n === i;
      slide.style.opacity = active ? '1' : '0';
      slide.style.zIndex = active ? '1' : '0';
      slide.toggleAttribute('data-active', active);

      const video = slide.querySelector('video');
      if (video) active ? video.play().catch(() => {}) : video.pause();
    });

    this.dots.forEach((dot, n) => {
      if (n === i) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('cs-hero')) {
  customElements.define('cs-hero', CsHero);
}

if (!customElements.get('collections-showcase-tabs')) {
  customElements.define('collections-showcase-tabs', CollectionsShowcaseTabs);
}

if (!customElements.get('cs-carousel')) {
  customElements.define('cs-carousel', CsCarousel);
}
